import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalculatorIcon, FileTextIcon } from 'lucide-react';
import { BillingModal } from './BillingModal';
import { TransactionFormModal } from './TransactionFormModal';
import { TransactionSuccessModal } from './TransactionSuccessModal';
import { useAuth } from '../contexts/AuthContext';
import { useTransactions } from '../contexts/TransactionContext';
type CommissionedParty = 'landlord' | 'tenant' | 'both';
type VATOption = 'included' | 'additional';
interface RentalCommissionCalculatorProps {
  onNavigateToTransactions?: () => void;
}
export function RentalCommissionCalculator({
  onNavigateToTransactions
}: RentalCommissionCalculatorProps) {
  const [transactionValue, setTransactionValue] = useState<string>('');
  const [commissionedParty, setCommissionedParty] = useState<CommissionedParty>('both');
  const [landlordCommission, setLandlordCommission] = useState<string>('50');
  const [tenantCommission, setTenantCommission] = useState<string>('50');
  const [vatOption, setVATOption] = useState<VATOption>('additional');
  const [landlordVATOption, setLandlordVATOption] = useState<VATOption>('additional');
  const [tenantVATOption, setTenantVATOption] = useState<VATOption>('additional');
  const [showResult, setShowResult] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const {
    user
  } = useAuth();
  const {
    addTransaction
  } = useTransactions();
  const calculateCommission = () => {
    if (parseFloat(transactionValue) > 0) {
      setShowResult(true);
    }
  };
  const handleAddTransaction = () => {
    const transaction = {
      type: 'rental' as const,
      transactionValue: transactionNum,
      agreedCommission: totalCommission,
      commissionedParty,
      clientName: 'Client Simulare',
      clientEmail: '',
      clientPhone: '',
      propertyAddress: 'Adresă de completat',
      propertyDetails: `Închiriere - Simulare`,
      notes: 'Tranzacție adăugată din simulator',
      landlordCommission: landlordCommissionAmount,
      tenantCommission: tenantCommissionAmount,
      landlordVAT,
      tenantVAT,
      landlordTotal,
      tenantTotal,
      vatOption,
      landlordVATOption,
      tenantVATOption,
      agentName: user?.fullName || 'Agent'
    };
    addTransaction(transaction);
    setShowSuccessModal(true);
  };
  const handleViewTransactions = () => {
    setShowSuccessModal(false);
    if (onNavigateToTransactions) {
      onNavigateToTransactions();
    }
  };
  const transactionNum = parseFloat(transactionValue) || 0;
  const landlordCommNum = parseFloat(landlordCommission) || 0;
  const tenantCommNum = parseFloat(tenantCommission) || 0;
  let landlordCommissionAmount = 0;
  let tenantCommissionAmount = 0;
  let landlordVAT = 0;
  let tenantVAT = 0;
  let landlordTotal = 0;
  let tenantTotal = 0;
  let totalCommission = 0;
  let totalVAT = 0;
  let finalTotal = 0;
  if (commissionedParty === 'landlord') {
    landlordCommissionAmount = transactionNum * landlordCommNum / 100;
    totalCommission = landlordCommissionAmount;
    if (vatOption === 'included') {
      // Reverse calculate: if VAT is included, the total is 121% (100% + 21%)
      const baseCommission = totalCommission / 1.21;
      totalVAT = totalCommission - baseCommission;
      finalTotal = totalCommission;
      landlordCommissionAmount = baseCommission;
    } else {
      totalVAT = totalCommission * 0.21;
      finalTotal = totalCommission + totalVAT;
    }
  } else if (commissionedParty === 'tenant') {
    tenantCommissionAmount = transactionNum * tenantCommNum / 100;
    totalCommission = tenantCommissionAmount;
    if (vatOption === 'included') {
      // Reverse calculate: if VAT is included, the total is 121% (100% + 21%)
      const baseCommission = totalCommission / 1.21;
      totalVAT = totalCommission - baseCommission;
      finalTotal = totalCommission;
      tenantCommissionAmount = baseCommission;
    } else {
      totalVAT = totalCommission * 0.21;
      finalTotal = totalCommission + totalVAT;
    }
  } else {
    // Calcul separat pentru fiecare parte cu opțiuni TVA separate
    landlordCommissionAmount = transactionNum * landlordCommNum / 100;
    tenantCommissionAmount = transactionNum * tenantCommNum / 100;
    if (landlordVATOption === 'included') {
      const baseCommission = landlordCommissionAmount / 1.21;
      landlordVAT = landlordCommissionAmount - baseCommission;
      landlordTotal = landlordCommissionAmount;
      landlordCommissionAmount = baseCommission;
    } else {
      landlordVAT = landlordCommissionAmount * 0.21;
      landlordTotal = landlordCommissionAmount + landlordVAT;
    }
    if (tenantVATOption === 'included') {
      const baseCommission = tenantCommissionAmount / 1.21;
      tenantVAT = tenantCommissionAmount - baseCommission;
      tenantTotal = tenantCommissionAmount;
      tenantCommissionAmount = baseCommission;
    } else {
      tenantVAT = tenantCommissionAmount * 0.21;
      tenantTotal = tenantCommissionAmount + tenantVAT;
    }
    totalCommission = landlordCommissionAmount + tenantCommissionAmount;
    totalVAT = landlordVAT + tenantVAT;
    finalTotal = landlordTotal + tenantTotal;
  }
  return <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-6489">
        {/* Calculator Input */}
        <div className="bg-white rounded-2xl p-8 shadow-xl" data-id="element-6490">
          <div className="flex items-center space-x-3 mb-6" data-id="element-6491">
            <CalculatorIcon className="w-8 h-8 text-[#21A366]" data-id="element-6492" />
            <h2 className="text-2xl font-bold text-[#132C53]" data-id="element-6493">
              Calculator Închiriere
            </h2>
          </div>
          <div className="space-y-6" data-id="element-6494">
            {/* Transaction Value */}
            <div data-id="element-6495">
              <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6496">
                Valoare chirie lunară (€)
              </label>
              <input type="number" value={transactionValue} onChange={e => setTransactionValue(e.target.value)} className="w-full text-3xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#21A366]" placeholder="600" min="0" data-id="element-6497" />
            </div>
            {/* Commissioned Party */}
            <div data-id="element-6498">
              <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6499">
                Părți comisionate
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-6500">
                {[{
                value: 'landlord',
                label: 'Proprietar'
              }, {
                value: 'tenant',
                label: 'Chiriaș'
              }, {
                value: 'both',
                label: 'Ambele'
              }].map(option => <button key={option.value} onClick={() => setCommissionedParty(option.value as CommissionedParty)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${commissionedParty === option.value ? 'border-[#21A366] bg-[#21A366] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6501">
                    {option.label}
                  </button>)}
              </div>
            </div>
            {/* Landlord Commission */}
            {(commissionedParty === 'landlord' || commissionedParty === 'both') && <div className="space-y-4" data-id="element-6502">
                <div data-id="element-6503">
                  <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6504">
                    Comision proprietar (%)
                  </label>
                  <input type="number" value={landlordCommission} onChange={e => setLandlordCommission(e.target.value)} className="w-full text-2xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#21A366]" placeholder="50" min="0" max="100" data-id="element-6505" />
                </div>
                {commissionedParty === 'both' && <div data-id="element-6506">
                    <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6507">
                      Opțiune TVA proprietar
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-6508">
                      {[{
                  value: 'included',
                  label: 'TVA inclus'
                }, {
                  value: 'additional',
                  label: '+ TVA (21%)'
                }].map(option => <button key={option.value} onClick={() => setLandlordVATOption(option.value as VATOption)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${landlordVATOption === option.value ? 'border-[#21A366] bg-[#21A366] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6509">
                          {option.label}
                        </button>)}
                    </div>
                  </div>}
              </div>}
            {/* Tenant Commission */}
            {(commissionedParty === 'tenant' || commissionedParty === 'both') && <div className="space-y-4" data-id="element-6510">
                <div data-id="element-6511">
                  <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6512">
                    Comision chiriaș (%)
                  </label>
                  <input type="number" value={tenantCommission} onChange={e => setTenantCommission(e.target.value)} className="w-full text-2xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#21A366]" placeholder="50" min="0" max="100" data-id="element-6513" />
                </div>
                {commissionedParty === 'both' && <div data-id="element-6514">
                    <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6515">
                      Opțiune TVA chiriaș
                    </label>
                    <div className="grid grid-cols-2 gap-3" data-id="element-6516">
                      {[{
                  value: 'included',
                  label: 'TVA inclus'
                }, {
                  value: 'additional',
                  label: '+ TVA (21%)'
                }].map(option => <button key={option.value} onClick={() => setTenantVATOption(option.value as VATOption)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${tenantVATOption === option.value ? 'border-[#21A366] bg-[#21A366] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6517">
                          {option.label}
                        </button>)}
                    </div>
                  </div>}
              </div>}
            {/* VAT Option - Only for single party */}
            {commissionedParty !== 'both' && <div data-id="element-6518">
                <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-6519">
                  Opțiune TVA
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-6520">
                  {[{
                value: 'included',
                label: 'TVA inclus'
              }, {
                value: 'additional',
                label: '+ TVA (21%)'
              }].map(option => <button key={option.value} onClick={() => setVATOption(option.value as VATOption)} className={`p-3 rounded-xl border-2 font-semibold transition-all ${vatOption === option.value ? 'border-[#21A366] bg-[#21A366] text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-id="element-6521">
                      {option.label}
                    </button>)}
                </div>
              </div>}
            <button onClick={calculateCommission} disabled={!transactionValue || parseFloat(transactionValue) <= 0} className="w-full bg-gradient-to-r from-[#21A366] to-[#1a8555] text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-shadow disabled:opacity-50" data-id="element-6522">
              Calculează comision
            </button>
          </div>
        </div>
        {/* Results */}
        <div className="space-y-6" data-id="element-6523">
          {showResult && transactionNum > 0 ? <>
              <div className="bg-white rounded-2xl p-8 shadow-xl" data-id="element-6524">
                <h2 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-6525">
                  Rezultat calcul
                </h2>
                <div className="space-y-4" data-id="element-6526">
                  <div className="bg-gray-50 rounded-xl p-4" data-id="element-6527">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-6528">
                      Valoare tranzacție
                    </p>
                    <p className="text-2xl font-bold text-gray-700" data-id="element-6529">
                      {transactionNum.toLocaleString('ro-RO')}€
                    </p>
                  </div>
                  {commissionedParty === 'both' ? <>
                      <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200" data-id="element-6530">
                        <p className="text-sm text-gray-600 mb-3 font-semibold" data-id="element-6531">
                          Calcul detaliat pe părți:
                        </p>
                        <div className="space-y-4" data-id="element-6532">
                          {/* Proprietar */}
                          <div className="pb-3 border-b border-purple-200" data-id="element-6533">
                            <div className="flex justify-between items-center mb-2" data-id="element-6534">
                              <span className="text-sm font-semibold text-gray-700" data-id="element-6535">
                                Comision proprietar ({landlordCommNum}%)
                              </span>
                              <span className="font-bold text-purple-700" data-id="element-6536">
                                {landlordCommissionAmount.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                            {landlordVATOption === 'additional' && <div className="flex justify-between items-center text-sm" data-id="element-6537">
                                <span className="text-gray-600" data-id="element-6538">
                                  + TVA (21%)
                                </span>
                                <span className="font-semibold text-purple-600" data-id="element-6539">
                                  {landlordVAT.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                  €
                                </span>
                              </div>}
                            {landlordVATOption === 'included' && <div className="text-sm space-y-1 mb-2" data-id="element-6540">
                                <div className="flex justify-between items-center text-gray-600" data-id="element-6541">
                                  <span data-id="element-6542">TVA inclus (21%):</span>
                                  <span className="font-semibold text-purple-600" data-id="element-6543">
                                    {landlordVAT.toLocaleString('ro-RO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                                    €
                                  </span>
                                </div>
                              </div>}
                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-purple-100" data-id="element-6544">
                              <span className="text-sm font-bold text-gray-700" data-id="element-6545">
                                Total proprietar
                              </span>
                              <span className="font-bold text-purple-800" data-id="element-6546">
                                {landlordTotal.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                          </div>
                          {/* Chiriaș */}
                          <div data-id="element-6547">
                            <div className="flex justify-between items-center mb-2" data-id="element-6548">
                              <span className="text-sm font-semibold text-gray-700" data-id="element-6549">
                                Comision chiriaș ({tenantCommNum}%)
                              </span>
                              <span className="font-bold text-purple-700" data-id="element-6550">
                                {tenantCommissionAmount.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                            {tenantVATOption === 'additional' && <div className="flex justify-between items-center text-sm" data-id="element-6551">
                                <span className="text-gray-600" data-id="element-6552">
                                  + TVA (21%)
                                </span>
                                <span className="font-semibold text-purple-600" data-id="element-6553">
                                  {tenantVAT.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                  €
                                </span>
                              </div>}
                            {tenantVATOption === 'included' && <div className="text-sm space-y-1 mb-2" data-id="element-6554">
                                <div className="flex justify-between items-center text-gray-600" data-id="element-6555">
                                  <span data-id="element-6556">TVA inclus (21%):</span>
                                  <span className="font-semibold text-purple-600" data-id="element-6557">
                                    {tenantVAT.toLocaleString('ro-RO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                                    €
                                  </span>
                                </div>
                              </div>}
                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-purple-100" data-id="element-6558">
                              <span className="text-sm font-bold text-gray-700" data-id="element-6559">
                                Total chiriaș
                              </span>
                              <span className="font-bold text-purple-800" data-id="element-6560">
                                {tenantTotal.toLocaleString('ro-RO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                                €
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </> : <>
                      <div className="bg-blue-50 rounded-xl p-4" data-id="element-6561">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-6562">
                          Comision fără TVA
                        </p>
                        <p className="text-2xl font-bold text-[#357BEB]" data-id="element-6563">
                          {(commissionedParty === 'landlord' ? landlordCommissionAmount : tenantCommissionAmount).toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                          €
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-4" data-id="element-6564">
                        <p className="text-sm text-gray-600 mb-1" data-id="element-6565">
                          {vatOption === 'included' ? 'TVA inclus (21%)' : 'TVA (21%)'}
                        </p>
                        <p className="text-2xl font-bold text-orange-600" data-id="element-6566">
                          {totalVAT.toLocaleString('ro-RO', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                          €
                        </p>
                      </div>
                    </>}
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200" data-id="element-6567">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-6568">
                      Total general de facturat
                    </p>
                    <p className="text-3xl font-bold text-green-700" data-id="element-6569">
                      {finalTotal.toLocaleString('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      €
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200" data-id="element-6570">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-6571">
                      Sumă eligibilă pentru comisionare (fără TVA)
                    </p>
                    <p className="text-3xl font-bold text-blue-700" data-id="element-6572">
                      {totalCommission.toLocaleString('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      €
                    </p>
                  </div>
                </div>
              </div>
              {user ? <button onClick={handleAddTransaction} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-all flex items-center justify-center space-x-2" data-id="element-6573">
                  <FileTextIcon className="w-5 h-5" data-id="element-6574" />
                  <span data-id="element-6575">Adaugă tranzacție</span>
                </button> : <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 text-center" data-id="element-6576">
                  <p className="text-gray-700 mb-4" data-id="element-6577">
                    Trebuie să fii autentificat pentru a adăuga tranzacții
                  </p>
                  <a href="/auth/login" className="inline-flex items-center space-x-2 bg-[#357BEB] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-6578">
                    <span data-id="element-6579">Loghează-te pentru a adăuga tranzacții</span>
                  </a>
                </div>}
            </> : <div className="bg-white rounded-2xl p-12 shadow-xl text-center" data-id="element-6580">
              <div className="text-6xl mb-4" data-id="element-6581">🏠</div>
              <h3 className="text-xl font-bold text-[#132C53] mb-2" data-id="element-6582">
                Completează datele pentru calcul
              </h3>
            </div>}
        </div>
      </div>
      {showTransactionForm && user && <TransactionFormModal transactionType="rental" transactionValue={transactionNum} agreedCommission={totalCommission} commissionedParty={commissionedParty} landlordCommission={landlordCommissionAmount} tenantCommission={tenantCommissionAmount} landlordVAT={landlordVAT} tenantVAT={tenantVAT} landlordTotal={landlordTotal} tenantTotal={tenantTotal} landlordVATOption={landlordVATOption} tenantVATOption={tenantVATOption} vatOption={vatOption} onClose={() => setShowTransactionForm(false)} onBack={() => setShowTransactionForm(false)} data-id="element-6583" />}
      <TransactionSuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} onViewTransactions={handleViewTransactions} data-id="element-6584" />
    </>;
}